import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { coachServiceFixed as coachService } from '@/services/coachServiceFixed';
import { useTeams } from '@/hooks/useTeams';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';

interface PerformanceTrackingModalProps {
  open: boolean;
  onClose: () => void;
  onMetricAdded: () => void;
}

const PerformanceTrackingModal: React.FC<PerformanceTrackingModalProps> = ({ 
  open, 
  onClose, 
  onMetricAdded 
}) => {
  const { profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const { teams } = useTeams();
  const [loading, setLoading] = useState(false);

  const currentUserId = profile?.id || internalUser?.id;
  const userTeams = teams.filter(team => team.created_by === currentUserId);

  const [formData, setFormData] = useState({
    team_id: '',
    player_id: '',
    metric_type: 'attendance' as 'attendance' | 'performance_score' | 'skill_rating' | 'team_satisfaction',
    metric_value: '',
    measurement_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserId) return;

    try {
      setLoading(true);
      
      const metricData = {
        coach_id: currentUserId,
        team_id: parseInt(formData.team_id),
        player_id: formData.player_id || undefined,
        metric_type: formData.metric_type,
        metric_value: parseFloat(formData.metric_value),
        measurement_date: formData.measurement_date,
        notes: formData.notes || undefined
      };

      // Note: Performance metric functionality not yet implemented in coachServiceFixed
      // await coachService.createPerformanceMetric(metricData);
      toast.success('Performance tracking feature coming soon!');
      onMetricAdded();
      onClose();
      
      // Reset form
      setFormData({
        team_id: '',
        player_id: '',
        metric_type: 'attendance',
        metric_value: '',
        measurement_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
    } catch (error) {
      console.error('Error recording metric:', error);
      toast.error('Failed to record performance metric');
    } finally {
      setLoading(false);
    }
  };

  const getMetricDescription = (type: string) => {
    switch (type) {
      case 'attendance':
        return 'Percentage (0-100)';
      case 'performance_score':
        return 'Score (0-100)';
      case 'skill_rating':
        return 'Rating (1-10)';
      case 'team_satisfaction':
        return 'Rating (1-5)';
      default:
        return '';
    }
  };

  const getMaxValue = (type: string) => {
    switch (type) {
      case 'attendance':
      case 'performance_score':
        return 100;
      case 'skill_rating':
        return 10;
      case 'team_satisfaction':
        return 5;
      default:
        return 100;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0d1117] border border-gray-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sports-green">Record Performance Metric</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="team_id">Team</Label>
            <Select 
              value={formData.team_id} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, team_id: value }))}
            >
              <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                {userTeams.map((team) => (
                  <SelectItem key={team.team_id} value={team.team_id.toString()}>
                    {team.team_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="metric_type">Metric Type</Label>
            <Select 
              value={formData.metric_type} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, metric_type: value as any }))}
            >
              <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="attendance">Attendance</SelectItem>
                <SelectItem value="performance_score">Performance Score</SelectItem>
                <SelectItem value="skill_rating">Skill Rating</SelectItem>
                <SelectItem value="team_satisfaction">Team Satisfaction</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="metric_value">
              Value ({getMetricDescription(formData.metric_type)})
            </Label>
            <Input
              id="metric_value"
              type="number"
              min="0"
              max={getMaxValue(formData.metric_type)}
              step={formData.metric_type === 'team_satisfaction' ? '0.1' : '1'}
              value={formData.metric_value}
              onChange={(e) => setFormData(prev => ({ ...prev, metric_value: e.target.value }))}
              className="bg-gray-900 border-gray-700 text-white"
              required
            />
          </div>

          <div>
            <Label htmlFor="measurement_date">Date</Label>
            <Input
              id="measurement_date"
              type="date"
              value={formData.measurement_date}
              onChange={(e) => setFormData(prev => ({ ...prev, measurement_date: e.target.value }))}
              className="bg-gray-900 border-gray-700 text-white"
              required
            />
          </div>

          <div>
            <Label htmlFor="player_id">Player ID (Optional)</Label>
            <Input
              id="player_id"
              value={formData.player_id}
              onChange={(e) => setFormData(prev => ({ ...prev, player_id: e.target.value }))}
              className="bg-gray-900 border-gray-700 text-white"
              placeholder="Leave empty for team-wide metrics"
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="bg-gray-900 border-gray-700 text-white"
              rows={3}
              placeholder="Additional observations or context..."
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-sports-green text-black hover:bg-green-600"
            >
              {loading ? 'Recording...' : 'Record Metric'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PerformanceTrackingModal;